import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    Card,
    CardContent,
    Grid,
    Chip,
    Button,
    IconButton,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useTheme,
    useMediaQuery,
    CircularProgress,
    Alert,
    Tabs,
    Tab
} from '@mui/material';
import {
    Palette as PaletteIcon,
    Person as PersonIcon,
    Share as ShareIcon,
    Favorite as FavoriteIcon,
    FavoriteBorder as FavoriteBorderIcon,
    BarChart as BarChartIcon,
    ArrowBack as ArrowBackIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    ContentCopy as ContentCopyIcon,
    Add as AddIcon
} from '@mui/icons-material';
import { getDeckById, getUserData } from '../../services/tracker-limited-data';
import { useAuth } from '../../contexts/AuthContext';
import { InkColorIcons } from './InkColorIcon';
import DeckVisual from './DeckVisual';
import DeckPreviewModal from './DeckPreviewModal';
import QuickMatchForm from './QuickMatchForm';

const cardStyle = {
    background: 'rgba(17, 24, 39, 0.85)',
    backdropFilter: 'blur(16px)',
    border: '1px solid rgba(76, 29, 149, 0.25)',
    borderRadius: '12px',
    transition: 'all 0.2s ease',
    '&:hover': {
        borderColor: 'rgba(124, 58, 237, 0.4)',
        boxShadow: '0 0 20px rgba(124, 58, 237, 0.08)'
    }
};

function DeckDetailsPage() {
    const { deckId } = useParams();
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [deck, setDeck] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isDeckFavorite, setIsDeckFavorite] = useState(false);
    const [activeTab, setActiveTab] = useState('archetypes');
    const [previewOpen, setPreviewOpen] = useState(false);
    const [addMatchOpen, setAddMatchOpen] = useState(false);

    useEffect(() => {
        const loadDeckDetails = async () => {
            setLoading(true);
            setError(null);
            try {
                const deckData = await getDeckById(deckId);
                if (!deckData) {
                    setError('Talia nie została znaleziona');
                    return;
                }
                setDeck(deckData);
                if (currentUser) {
                    const userData = await getUserData(currentUser.uid);
                    if (userData) {
                        setIsDeckFavorite(userData.favoriteDecks?.includes(deckId) || false);
                    }
                }
            } catch (err) {
                console.error('Błąd podczas ładowania danych talii:', err);
                setError('Wystąpił błąd podczas ładowania danych talii');
            } finally {
                setLoading(false);
            }
        };
        loadDeckDetails();
    }, [deckId, currentUser]);

    useEffect(() => {
        if (!deck) return;
        const vsA = Object.keys(deck.stats?.vsArchetypes || {}).length > 0;
        const vsC = Object.keys(deck.stats?.vsColors || {}).length > 0;
        const perP = Object.keys(deck.stats?.perPlayer || {}).length > 0;
        const tabOrder = [vsA && 'archetypes', vsC && 'colors', perP && 'players'].filter(Boolean);
        if (tabOrder.length && !tabOrder.includes(activeTab)) {
            setActiveTab(tabOrder[0]);
        }
    }, [deck]);

    const getWinrateColor = (winrate) => {
        if (winrate >= 60) return '#10b981';
        if (winrate >= 50) return '#f59e0b';
        return '#ef4444';
    };

    const getWinrateBg = (winrate) => {
        if (winrate >= 60) return 'rgba(16, 185, 129, 0.15)';
        if (winrate >= 50) return 'rgba(245, 158, 11, 0.12)';
        return 'rgba(239, 68, 68, 0.15)';
    };

    const formatColorName = (color) => {
        const colorMap = {
            'amber': 'Amber',
            'amethyst': 'Amethyst',
            'steel': 'Steel',
            'ruby': 'Ruby',
            'sapphire': 'Sapphire',
            'emerald': 'Emerald'
        };
        return colorMap[(color || '').toLowerCase()] || color;
    };

    const handleFavoriteToggle = () => setIsDeckFavorite(!isDeckFavorite);
    const handleShare = () => navigator.clipboard.writeText(window.location.href);
    const handleCopyList = () => { if (deck?.cards) navigator.clipboard.writeText(deck.cards); };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                    <CircularProgress sx={{ color: '#7c3aed' }} />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                <Button onClick={() => navigate(-1)} variant="outlined">
                    Powrót
                </Button>
            </Container>
        );
    }

    if (!deck) return null;

    const { stats } = deck;
    const overall = stats?.overall || {};
    const vsArchetypes = stats?.vsArchetypes || {};
    const vsColors = stats?.vsColors || {};
    const perPlayer = stats?.perPlayer || {};

    const hasVsArchetypes = Object.keys(vsArchetypes).length > 0;
    const hasVsColors = Object.keys(vsColors).length > 0;
    const hasPerPlayer = Object.keys(perPlayer).length > 0;

    const tabOrder = [hasVsArchetypes && 'archetypes', hasVsColors && 'colors', hasPerPlayer && 'players'].filter(Boolean);
    const defaultTab = tabOrder[0] || 'archetypes';

    return (
        <Container maxWidth="xl" sx={{ py: isMobile ? 1.5 : 2, px: isSmallMobile ? 1.5 : 2 }}>
            {/* Header */}
            <Card sx={{ ...cardStyle, mb: 2 }}>
                {/* Górny pasek: back + nazwa + przyciski */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, pt: 1, pb: isSmallMobile ? 0.5 : 1 }}>
                    <IconButton
                        onClick={() => navigate(-1)}
                        size="small"
                        sx={{ color: 'rgba(196, 181, 253, 0.9)', flexShrink: 0, '&:hover': { bgcolor: 'rgba(124, 58, 237, 0.15)' } }}
                    >
                        <ArrowBackIcon sx={{ fontSize: 20 }} />
                    </IconButton>

                    {/* Na desktopie miniatura jest tu, inline */}
                    {!isSmallMobile && (
                        <Box
                            onClick={() => setPreviewOpen(true)}
                            sx={{
                                width: isMobile ? 140 : 180,
                                height: 64,
                                borderRadius: '8px',
                                overflow: 'hidden',
                                flexShrink: 0,
                                bgcolor: 'rgba(76, 29, 149, 0.15)',
                                border: '1px solid rgba(76, 29, 149, 0.3)',
                                cursor: 'pointer',
                                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                                '&:hover': { borderColor: 'rgba(124, 58, 237, 0.7)', boxShadow: '0 0 14px rgba(124, 58, 237, 0.25)' }
                            }}
                        >
                            <DeckVisual deck={deck} mode="thumbnail" />
                        </Box>
                    )}

                    <Box sx={{ flex: 1, minWidth: 0, px: 0.5 }}>
                        <Typography
                            variant="h6"
                            sx={{ color: 'white', fontWeight: 600, fontSize: isSmallMobile ? '0.9rem' : '1.05rem', letterSpacing: '-0.01em', lineHeight: 1.2 }}
                            noWrap
                        >
                            {deck.name}
                        </Typography>
                        {deck.archetype && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                <InkColorIcons colors={deck.archetype.ink_colors || []} size={14} gap={0.2} />
                                <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)', fontSize: '0.7rem' }} noWrap>
                                    {deck.archetype.name}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 0, flexShrink: 0, alignItems: 'center' }}>
                        {currentUser && (
                            <Tooltip title="Dodaj mecz">
                                <IconButton size="small" onClick={() => setAddMatchOpen(true)} sx={{ color: 'rgba(196, 181, 253, 0.75)', '&:hover': { bgcolor: 'rgba(124, 58, 237, 0.15)' } }}>
                                    <AddIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                        {deck?.cards && (
                            <Tooltip title="Kopiuj listę kart">
                                <IconButton size="small" onClick={handleCopyList} sx={{ color: 'rgba(196, 181, 253, 0.75)', '&:hover': { bgcolor: 'rgba(124, 58, 237, 0.15)' } }}>
                                    <ContentCopyIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title="Udostępnij">
                            <IconButton size="small" onClick={handleShare} sx={{ color: 'rgba(196, 181, 253, 0.75)', '&:hover': { bgcolor: 'rgba(124, 58, 237, 0.15)' } }}>
                                <ShareIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                        {currentUser && (
                            <Tooltip title={isDeckFavorite ? 'Usuń z ulubionych' : 'Dodaj do ulubionych'}>
                                <IconButton size="small" onClick={handleFavoriteToggle} sx={{ color: isDeckFavorite ? '#ef4444' : 'rgba(196, 181, 253, 0.75)', '&:hover': { bgcolor: 'rgba(124, 58, 237, 0.15)' } }}>
                                    {isDeckFavorite ? <FavoriteIcon sx={{ fontSize: 18 }} /> : <FavoriteBorderIcon sx={{ fontSize: 18 }} />}
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </Box>

                {/* Na mobile: panoramiczna miniatura pod paskiem, pełna szerokość */}
                {isSmallMobile && (
                    <Box
                        onClick={() => setPreviewOpen(true)}
                        sx={{
                            mx: 1, mb: 1,
                            height: 56,
                            borderRadius: '8px',
                            overflow: 'hidden',
                            bgcolor: 'rgba(76, 29, 149, 0.15)',
                            border: '1px solid rgba(76, 29, 149, 0.3)',
                            cursor: 'pointer',
                            transition: 'border-color 0.2s ease',
                            '&:hover': { borderColor: 'rgba(124, 58, 237, 0.6)' }
                        }}
                    >
                        <DeckVisual deck={deck} mode="thumbnail" />
                    </Box>
                )}
            </Card>

            {/* Modal podglądu talii */}
            <DeckPreviewModal open={previewOpen} onClose={() => setPreviewOpen(false)} deck={deck} />

            {/* Modal dodawania meczu */}
            <QuickMatchForm
                open={addMatchOpen}
                onClose={() => setAddMatchOpen(false)}
                preselectedDeckId={deckId}
                onMatchAdded={() => setAddMatchOpen(false)}
            />

            {/* Statystyki */}
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={3}>
                    <Card sx={{ ...cardStyle, height: '100%' }}>
                        <CardContent sx={{ p: 1.5, textAlign: 'center', '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="h5" sx={{ color: getWinrateColor(overall.winrate || 0), fontWeight: 700, fontSize: isSmallMobile ? '1.35rem' : '1.5rem', letterSpacing: '-0.02em' }}>
                                {overall.winrate || 0}%
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.75)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Winrate
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={3}>
                    <Card sx={{ ...cardStyle, height: '100%' }}>
                        <CardContent sx={{ p: 1.5, textAlign: 'center', '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, fontSize: isSmallMobile ? '1.35rem' : '1.5rem', letterSpacing: '-0.02em' }}>
                                {overall.games || 0}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.75)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Gier
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={3}>
                    <Card sx={{ ...cardStyle, height: '100%' }}>
                        <CardContent sx={{ p: 1.5, textAlign: 'center', '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                <TrendingUpIcon sx={{ color: '#10b981', fontSize: 22 }} />
                                <Typography variant="h5" sx={{ color: '#10b981', fontWeight: 700, fontSize: isSmallMobile ? '1.35rem' : '1.5rem', letterSpacing: '-0.02em' }}>
                                    {overall.wins || 0}
                                </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.75)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Wygrane
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={3}>
                    <Card sx={{ ...cardStyle, height: '100%' }}>
                        <CardContent sx={{ p: 1.5, textAlign: 'center', '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                <TrendingDownIcon sx={{ color: '#ef4444', fontSize: 22 }} />
                                <Typography variant="h5" sx={{ color: '#ef4444', fontWeight: 700, fontSize: isSmallMobile ? '1.35rem' : '1.5rem', letterSpacing: '-0.02em' }}>
                                    {overall.losses || 0}
                                </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.75)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Przegrane
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Play vs Draw + Dreamborn */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
                {overall.play && overall.draw && (
                    <>
                        <Chip
                            size="small"
                            label={`Play ${overall.play.winrate}% · ${overall.play.games} gier`}
                            sx={{
                                bgcolor: 'rgba(16, 185, 129, 0.12)',
                                color: '#10b981',
                                border: '1px solid rgba(16, 185, 129, 0.3)',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                borderRadius: '8px'
                            }}
                        />
                        <Chip
                            size="small"
                            label={`Draw ${overall.draw.winrate}% · ${overall.draw.games} gier`}
                            sx={{
                                bgcolor: 'rgba(59, 130, 246, 0.12)',
                                color: '#3b82f6',
                                border: '1px solid rgba(59, 130, 246, 0.3)',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                borderRadius: '8px'
                            }}
                        />
                    </>
                )}
                {deck.dreamborn_url && (
                    <Button
                        variant="outlined"
                        size="small"
                        href={deck.dreamborn_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            color: 'rgba(196, 181, 253, 0.9)',
                            borderColor: 'rgba(76, 29, 149, 0.4)',
                            fontSize: '0.75rem',
                            textTransform: 'none',
                            '&:hover': { borderColor: 'rgba(124, 58, 237, 0.6)', bgcolor: 'rgba(124, 58, 237, 0.08)' }
                        }}
                    >
                        Dreamborn →
                    </Button>
                )}
            </Box>

            {/* Tabele */}
            {(hasVsArchetypes || hasVsColors || hasPerPlayer) ? (
                <Card sx={{ ...cardStyle, overflow: 'hidden' }}>
                    <Tabs
                        value={tabOrder.includes(activeTab) ? activeTab : defaultTab}
                        onChange={(_, v) => setActiveTab(v)}
                        sx={{
                            minHeight: 40,
                            px: 1,
                            borderBottom: '1px solid rgba(76, 29, 149, 0.2)',
                            '& .MuiTab-root': {
                                minHeight: 40,
                                py: 0,
                                fontSize: '0.8rem',
                                color: 'rgba(196, 181, 253, 0.6)',
                                textTransform: 'none',
                                fontWeight: 500
                            },
                            '& .Mui-selected': { color: '#c4b5fd' },
                            '& .MuiTabs-indicator': { backgroundColor: '#7c3aed', height: 2 }
                        }}
                    >
                        {hasVsArchetypes && <Tab value="archetypes" icon={<BarChartIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="vs Archetypy" />}
                        {hasVsColors && <Tab value="colors" icon={<PaletteIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="vs Kolory" />}
                        {hasPerPlayer && <Tab value="players" icon={<PersonIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Per Gracz" />}
                    </Tabs>
                    <CardContent sx={{ p: 0, pt: 0, '&:last-child': { pb: 0 } }}>
                        {activeTab === 'archetypes' && hasVsArchetypes && (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: 'rgba(17, 24, 39, 0.98)', color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.75rem', py: 0.75, borderBottom: '1px solid rgba(76, 29, 149, 0.2)' }}>Archetyp</TableCell>
                                            <TableCell sx={{ bgcolor: 'rgba(17, 24, 39, 0.98)', color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.75rem', textAlign: 'center', py: 0.75, borderBottom: '1px solid rgba(76, 29, 149, 0.2)' }}>Play</TableCell>
                                            <TableCell sx={{ bgcolor: 'rgba(17, 24, 39, 0.98)', color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.75rem', textAlign: 'center', py: 0.75, borderBottom: '1px solid rgba(76, 29, 149, 0.2)' }}>Draw</TableCell>
                                            <TableCell sx={{ bgcolor: 'rgba(17, 24, 39, 0.98)', color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.75rem', textAlign: 'center', py: 0.75, borderBottom: '1px solid rgba(76, 29, 149, 0.2)' }}>Gier</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Object.entries(vsArchetypes).sort(([, a], [, b]) => b.games - a.games).map(([archetypeId, s]) => {
                                            const isLowSample = (s.games || 0) < 5;
                                            return (
                                            <TableRow
                                                key={archetypeId}
                                                onClick={() => navigate(`/archetype/${archetypeId}`)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    bgcolor: isLowSample ? 'rgba(107, 114, 128, 0.12)' : getWinrateBg(s.winrate),
                                                    opacity: isLowSample ? 0.85 : 1,
                                                    borderBottom: '1px solid rgba(76, 29, 149, 0.1)',
                                                    transition: 'background 0.15s ease',
                                                    '&:hover': { filter: 'brightness(1.1)' },
                                                    '&:last-child': { borderBottom: 'none' }
                                                }}
                                            >
                                                <TableCell sx={{ py: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'rgba(0, 0, 0, 0.35)', borderRadius: 1, px: 0.75, py: 0.25, border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                                                                <InkColorIcons colors={s.colors || []} size={24} gap={0.4} />
                                                            </Box>
                                                            <Box>
                                                                <Typography variant="body2" sx={{ color: isLowSample ? 'rgba(196, 181, 253, 0.6)' : 'white', fontSize: '0.8rem', fontWeight: 600 }} noWrap>{s.name}</Typography>
                                                                {isLowSample && <Typography variant="caption" sx={{ color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.65rem', display: 'block' }}>Za mało meczy (min. 5)</Typography>}
                                                            </Box>
                                                        </Box>
                                                        <Typography component="span" sx={{ color: isLowSample ? 'rgba(156, 163, 175, 0.85)' : getWinrateColor(s.winrate), fontWeight: isLowSample ? 500 : 700, fontSize: '0.85rem', flexShrink: 0 }}>
                                                            {s.winrate}%
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'center', color: isLowSample ? 'rgba(156, 163, 175, 0.75)' : 'rgba(255,255,255,0.9)', fontSize: '0.8rem', py: 1 }}>{s.play?.winrate ?? '—'}%</TableCell>
                                                <TableCell sx={{ textAlign: 'center', color: isLowSample ? 'rgba(156, 163, 175, 0.75)' : 'rgba(255,255,255,0.9)', fontSize: '0.8rem', py: 1 }}>{s.draw?.winrate ?? '—'}%</TableCell>
                                                <TableCell sx={{ textAlign: 'center', color: isLowSample ? 'rgba(156, 163, 175, 0.9)' : 'white', fontWeight: 600, fontSize: '0.8rem', py: 1 }}>{s.games}</TableCell>
                                            </TableRow>
                                        );})}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                        {activeTab === 'colors' && hasVsColors && (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: 'rgba(17, 24, 39, 0.98)', color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.75rem', py: 0.75, borderBottom: '1px solid rgba(76, 29, 149, 0.2)' }}>Kolory</TableCell>
                                            <TableCell sx={{ bgcolor: 'rgba(17, 24, 39, 0.98)', color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.75rem', textAlign: 'center', py: 0.75, borderBottom: '1px solid rgba(76, 29, 149, 0.2)' }}>Gier</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Object.entries(vsColors).sort(([, a], [, b]) => b.games - a.games).map(([colorCombo, s]) => {
                                            const isLowSample = (s.games || 0) < 5;
                                            return (
                                            <TableRow
                                                key={colorCombo}
                                                sx={{
                                                    bgcolor: isLowSample ? 'rgba(107, 114, 128, 0.12)' : getWinrateBg(s.winrate),
                                                    opacity: isLowSample ? 0.85 : 1,
                                                    borderBottom: '1px solid rgba(76, 29, 149, 0.1)',
                                                    '&:hover': { filter: 'brightness(1.1)' },
                                                    '&:last-child': { borderBottom: 'none' }
                                                }}
                                            >
                                                <TableCell sx={{ py: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'rgba(0, 0, 0, 0.35)', borderRadius: 1, px: 0.75, py: 0.25, border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                                                                <InkColorIcons colors={s.colors || []} size={24} gap={0.4} />
                                                            </Box>
                                                            <Box>
                                                                <Typography variant="body2" sx={{ color: isLowSample ? 'rgba(196, 181, 253, 0.6)' : 'white', fontSize: '0.8rem', fontWeight: 600 }}>{s.colors?.map(formatColorName).join(' / ')}</Typography>
                                                                {isLowSample && <Typography variant="caption" sx={{ color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.65rem', display: 'block' }}>Za mało meczy (min. 5)</Typography>}
                                                            </Box>
                                                        </Box>
                                                        <Typography component="span" sx={{ color: isLowSample ? 'rgba(156, 163, 175, 0.85)' : getWinrateColor(s.winrate), fontWeight: isLowSample ? 500 : 700, fontSize: '0.85rem' }}>
                                                            {s.winrate}%
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'center', color: isLowSample ? 'rgba(156, 163, 175, 0.85)' : 'white', fontWeight: 600, fontSize: '0.8rem', py: 1 }}>{s.games}</TableCell>
                                            </TableRow>
                                        );})}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                        {activeTab === 'players' && hasPerPlayer && (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: 'rgba(17, 24, 39, 0.98)', color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.75rem', py: 0.75, borderBottom: '1px solid rgba(76, 29, 149, 0.2)' }}>Gracz</TableCell>
                                            <TableCell sx={{ bgcolor: 'rgba(17, 24, 39, 0.98)', color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.75rem', textAlign: 'center', py: 0.75, borderBottom: '1px solid rgba(76, 29, 149, 0.2)' }}>W/L</TableCell>
                                            <TableCell sx={{ bgcolor: 'rgba(17, 24, 39, 0.98)', color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.75rem', textAlign: 'center', py: 0.75, borderBottom: '1px solid rgba(76, 29, 149, 0.2)' }}>Gier</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Object.entries(perPlayer).sort(([, a], [, b]) => b.games - a.games).map(([playerId, s]) => {
                                            const isLowSample = (s.games || 0) < 5;
                                            return (
                                            <TableRow
                                                key={playerId}
                                                sx={{
                                                    bgcolor: isLowSample ? 'rgba(107, 114, 128, 0.12)' : getWinrateBg(s.winrate),
                                                    opacity: isLowSample ? 0.85 : 1,
                                                    borderBottom: '1px solid rgba(76, 29, 149, 0.1)',
                                                    '&:hover': { filter: 'brightness(1.1)' },
                                                    '&:last-child': { borderBottom: 'none' }
                                                }}
                                            >
                                                <TableCell sx={{ py: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                                        <Box>
                                                            <Typography variant="body2" sx={{ color: isLowSample ? 'rgba(196, 181, 253, 0.6)' : 'white', fontSize: '0.8rem', fontWeight: 600 }}>{s.name}</Typography>
                                                            {isLowSample && <Typography variant="caption" sx={{ color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.65rem', display: 'block' }}>Za mało meczy (min. 5)</Typography>}
                                                        </Box>
                                                        <Typography component="span" sx={{ color: isLowSample ? 'rgba(156, 163, 175, 0.85)' : getWinrateColor(s.winrate), fontWeight: isLowSample ? 500 : 700, fontSize: '0.85rem' }}>
                                                            {s.winrate}%
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'center', color: isLowSample ? 'rgba(156, 163, 175, 0.75)' : 'rgba(255,255,255,0.9)', fontSize: '0.8rem', py: 1 }}>{s.wins} / {s.losses}</TableCell>
                                                <TableCell sx={{ textAlign: 'center', color: isLowSample ? 'rgba(156, 163, 175, 0.85)' : 'white', fontWeight: 600, fontSize: '0.8rem', py: 1 }}>{s.games}</TableCell>
                                            </TableRow>
                                        );})}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </CardContent>
                </Card>
            ) : (
                <Card sx={{ ...cardStyle, py: 4 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)', textAlign: 'center' }}>
                        Brak szczegółowych statystyk. Zarejestruj mecze z tą talią.
                    </Typography>
                </Card>
            )}
        </Container>
    );
}

export default DeckDetailsPage;
